/** The service registry: which products may sign people in through ryuauth. */

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/button";
import { PageHeader, panelClass, StatusBadge } from "@/components/display";
import { Empty, LoadError, Loading, useReportError } from "@/components/states";
import { useServices, useSetServiceEnabled } from "./hooks";
import { ServiceForm } from "./service-form";
import type { Service } from "./types";

export function ServicesPage() {
  const { data: services, isPending, isError, error } = useServices();
  const setEnabled = useSetServiceEnabled();
  const reportError = useReportError();
  // Which dialog is open: "new", a service being edited, or none. Local to this page.
  const [editing, setEditing] = useState<Service | "new" | null>(null);

  const registerButton = (
    <Button tone="primary" onClick={() => setEditing("new")}>
      <Plus size={16} /> Register service
    </Button>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Services" action={registerButton}
        description="Products that sign people in through ryuauth. Disabling one stops its logins and token calls at once." />
      <div className={panelClass}>
        {isPending ? (
          <Loading what="services" />
        ) : isError ? (
          <LoadError what="services" error={error} />
        ) : services.length === 0 ? (
          <Empty title="No services registered yet" action={registerButton}>
            Register each product with its redirect URIs and public key before it can sign anyone in.
          </Empty>
        ) : (
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {services.map((s) => (
              <li key={s.audience} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-zinc-950 dark:text-white">{s.name}</p>
                    <code className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                      {s.audience}
                    </code>
                    <StatusBadge status={s.active ? "active" : "inactive"}>{s.active ? "Active" : "Disabled"}</StatusBadge>
                  </div>
                  <ul className="space-y-0.5 font-mono text-xs text-zinc-500">
                    {s.redirect_uris.map((uri) => <li key={uri} className="truncate">{uri}</li>)}
                  </ul>
                  <p className="text-xs text-zinc-500">
                    May call for a user:{" "}
                    <span className="font-mono text-zinc-700 dark:text-zinc-300">{s.may_act_for.join(", ") || "no other service"}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setEditing(s)} aria-label={`Edit ${s.name}`}>Edit</Button>
                  <Button tone="ghost" aria-label={`${s.active ? "Disable" : "Enable"} ${s.name}`}
                    onClick={() => setEnabled.mutate({ audience: s.audience, enabled: !s.active }, { onError: reportError })}>
                    {s.active ? "Disable" : "Enable"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {editing !== null && (
        <ServiceForm key={editing === "new" ? "new" : editing.audience}
          service={editing === "new" ? null : editing} onDone={() => setEditing(null)} />
      )}
    </div>
  );
}
